import React, { createContext, useState, useCallback, useEffect } from 'react';
import {
  baseFilterFunctionMap,
  filterMap,
  queryParamResolver,
  sectionFilterInclusivity,
  reverseSectionMap,
  defaultFilterSetup,
} from './filterData.js';

export const FilterContext = createContext(null);

const getFiltersFromURLSearchParams = () => {
  const searchParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );
  const filters = Object.fromEntries(
    Object.keys(filterMap).map((key) => {
      return [key, { key, enabled: false }];
    })
  );

  const enableFilter = (filterValues, targetFilterName) => {
    const filterValue = filterValues.find(
      (filterValue) => filterValue.field === targetFilterName
    );
    if (filterValue) {
      filters[filterValue.originalField].enabled = true;
    }
  };

  Object.keys(searchParams).forEach((key) => {
    let isArray = false;
    if (searchParams[key].includes(',')) {
      isArray = true;
    }

    if (Object.prototype.hasOwnProperty.call(queryParamResolver, key)) {
      const filterValues = queryParamResolver[key];
      if (isArray) {
        const enabledSplitFilters = searchParams[key].split(',');
        enabledSplitFilters.forEach((splitFilter) => {
          enableFilter(filterValues, splitFilter);
        });
      } else {
        enableFilter(filterValues, searchParams[key]);
      }
    }
  });

  return filters;
};

const findFilterSection = (filterKey) => {
  return reverseSectionMap[filterKey];
};

const serializeFiltersToURLSearchParams = (filters) => {
  const enabledEntries = Object.values(filters)
    .filter((filter) => filter.enabled)
    .map((filter) => {
      const section = findFilterSection(filterMap[filter.key]).toLowerCase();
      return [
        section,
        queryParamResolver[section].find(
          (resolvedFilter) => resolvedFilter.originalField === filter.key
        ).field,
      ];
    });

  const deduplicatedEnabledEntries = [];
  enabledEntries.forEach(([entrySection, entryFilter]) => {
    const otherEntriesExist = enabledEntries.filter(
      ([searchEntrySection, _]) => entrySection === searchEntrySection
    ).length;

    if (otherEntriesExist === 1) {
      deduplicatedEnabledEntries.push([entrySection, entryFilter]);
      return;
    }

    const otherEntries =
      !deduplicatedEnabledEntries.find(
        ([existingEntrySection, _]) => existingEntrySection === entrySection
      ) &&
      enabledEntries.find(
        ([searchEntrySection, searchEntryFilter]) =>
          searchEntrySection === entrySection &&
          searchEntryFilter !== entryFilter
      );

    if (otherEntries) {
      deduplicatedEnabledEntries.push([
        entrySection,
        enabledEntries
          .map(([searchEntrySection, searchEntryFilter]) =>
            searchEntrySection === entrySection ? searchEntryFilter : false
          )
          .filter(Boolean),
      ]);
    }
  });
  return Object.fromEntries(deduplicatedEnabledEntries);
};

export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState(getFiltersFromURLSearchParams());

  useEffect(() => {
    const handleBackState = () => {
      const searchFilters = getFiltersFromURLSearchParams();
      setFilters(searchFilters);
    };

    window.addEventListener('popstate', handleBackState);

    return () => {
      window.removeEventListener('popstate', handleBackState);
    };
  }, []);

  const toggleFilter = (filterKey) => {
    const section = findFilterSection(filterMap[filterKey]);
    const enabledFilters = Object.values(filters).filter((searchFilter) => {
      const searchFilterSection = findFilterSection(
        filterMap[searchFilter.key]
      );
      return searchFilterSection === section && searchFilter.enabled;
    });

    const updatedFilters = { ...filters };

    if (filters[filterKey].enabled) {
      filters[filterKey].enabled = false;
    } else {
      if (sectionFilterInclusivity[section] === 'exclusive') {
        if (enabledFilters && !!enabledFilters.length) {
          // Disable the filters from the previous group
          enabledFilters.forEach((enabledFilter) => {
            updatedFilters[enabledFilter.key].enabled = false;
          });
        }
      }

      // Enable this and push the new page
      updatedFilters[filterKey].enabled = true;
    }

    setFilters(updatedFilters);
    const serializedFilters = serializeFiltersToURLSearchParams(updatedFilters);
    const serializedFiltersAsURLParams = new URLSearchParams(serializedFilters);

    window.history.pushState(
      updatedFilters,
      '',
      `?` + serializedFiltersAsURLParams.toString()
    );
  };

  const getEnabledFilters = useCallback(() => {
    return Object.values(filters).filter((filter) => filter.enabled);
  }, [filters]);

  const filterProducts = useCallback(
    (products) => {
      if (!products) return [];
      const enabledFilters = getEnabledFilters().map((filter) => filter.key);
      return products
        .map((product) => {
          let passFilters = true;

          enabledFilters.forEach((filterKey) => {
            const fn = baseFilterFunctionMap[filterKey];
            if (!fn(product)) {
              passFilters = false;
              return;
            }
          });

          if (passFilters) return product;
          return false;
        })
        .filter(Boolean);
    },
    [getEnabledFilters]
  );

  const clearFilters = useCallback(() => {
    setFilters(defaultFilterSetup);
    window.history.pushState('', '', window.location.pathname);
  }, [setFilters]);

  return (
    <FilterContext.Provider
      value={{
        filters,
        toggleFilter,
        getEnabledFilters,
        filterProducts,
        clearFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};
